/**
 * "Live stretch" source: the loop plays at its natural speed into a per-voice signalsmith-stretch node that shifts
 * the pitch in real time (duration and, optionally, formants preserved). Pitch changes are stepped through
 * schedule() at ~20 Hz, with the library's own block latency (~100 ms), so this is the experimental A/B mode
 * rather than the default. If the node cannot be created the unit falls back to plain resampling.
 */
import { audioLog, fmtHz } from './log';
import { glideTo } from './params';
import { loadStretch, withTimeout, type StretchNode } from './stretch';
import { effectiveHz, safeStop, type SourceUnit, type UnitConfig } from './units';

export interface StretchSample { buffer: AudioBuffer; loopStart: number; loopEnd: number; rootHz: number }

export class LiveStretchUnit implements SourceUnit {
  readonly output: GainNode;
  /** feeds the phase vocoder at natural speed */
  private readonly src: AudioBufferSourceNode;
  /** plays resampled from the very first sample until the vocoder is ready (or forever if it never is) */
  private readonly bypass: AudioBufferSourceNode;
  private readonly bypassGain: GainNode;
  private readonly nodeGain: GainNode;
  private node: StretchNode | null = null;
  private ready = false;
  private failed = false;
  private disposed = false;
  private started: { t: number; phase: number } | null = null;
  private semitones = 0;
  private lastScheduled = 0;
  private scheduleTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly modulators: AudioNode[] = [];

  constructor(private readonly cfg: UnitConfig, private readonly sample: StretchSample, private readonly formant: boolean) {
    const ctx = cfg.ctx;
    this.output = ctx.createGain();
    this.output.gain.value = cfg.level;
    const mk = () => { const s = ctx.createBufferSource(); s.buffer = sample.buffer; s.loop = true; s.loopStart = sample.loopStart; s.loopEnd = sample.loopEnd; return s; };
    this.src = mk();
    this.bypass = mk();
    this.bypassGain = ctx.createGain();
    this.bypassGain.gain.value = 1;
    this.nodeGain = ctx.createGain();
    this.nodeGain.gain.value = 0;
    this.bypass.connect(this.bypassGain);
    this.bypassGain.connect(this.output);
    this.nodeGain.connect(this.output);
    void this.init();
  }

  private async init(): Promise<void> {
    try {
      const factory = await loadStretch();
      const node = await withTimeout(factory(this.cfg.ctx, { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2] }), 8000, 'live stretch node');
      if (this.disposed) { try { node.disconnect(); } catch { /* ignore */ } return; }
      await node.start();
      await node.schedule({ semitones: this.semitones, tonalityHz: 8000, formantCompensation: this.formant, formantBaseHz: this.formant ? this.sample.rootHz : 0 });
      this.src.connect(node);
      node.connect(this.nodeGain);
      this.node = node;
      this.ready = true;
      const now = this.cfg.ctx.currentTime;
      if (this.started) {
        // the vocoder source joins at the loop phase the bypass has reached, then the two crossfade (0.25 s)
        const len = Math.max(0, this.sample.loopEnd - this.sample.loopStart);
        const rate = Math.pow(2, this.semitones / 12);
        const phase = len > 0 ? (this.started.phase + ((now - this.started.t) * rate) / len) % 1 : 0;
        this.src.start(now + 0.15, this.sample.loopStart + phase * len);   // ~vocoder latency ahead of the fade
        this.nodeGain.gain.setValueAtTime(0, now + 0.15);
        this.nodeGain.gain.linearRampToValueAtTime(1, now + 0.4);
        this.bypassGain.gain.setValueAtTime(1, now + 0.15);
        this.bypassGain.gain.linearRampToValueAtTime(0, now + 0.4);
        safeStop(this.bypass, now + 0.45);
      }
      audioLog.log('voice', () => `live-stretch node ready (${this.formant ? 'formant-locked' : 'plain'}), ${this.semitones.toFixed(2)} st — taking over from the resampled bypass`);
    } catch (e) {
      // no shifter: the resampled bypass simply keeps playing
      this.failed = true;
      audioLog.warn('voice', `live-stretch unavailable, staying resampled: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  private pushSchedule(): void {
    if (!this.node || this.disposed) return;
    const now = performance.now();
    const due = Math.max(0, 50 - (now - this.lastScheduled));
    if (this.scheduleTimer) return;
    this.scheduleTimer = setTimeout(() => {
      this.scheduleTimer = null;
      this.lastScheduled = performance.now();
      void this.node?.schedule({ semitones: this.semitones }).catch(() => undefined);
    }, due);
  }

  start(t: number, hz: number): void {
    this.semitones = 12 * Math.log2(effectiveHz(hz, this.cfg.cents) / this.sample.rootHz);
    const len = Math.max(0, this.sample.loopEnd - this.sample.loopStart);
    const phase = ((this.cfg.seed % 1000) / 1000) * this.cfg.phaseScatter;
    this.bypass.detune.value = this.semitones * 100;
    this.bypass.start(t, this.sample.loopStart + phase * len);
    this.started = { t, phase };
    if (this.ready) { this.src.start(t, this.sample.loopStart + phase * len); this.nodeGain.gain.value = 1; this.bypassGain.gain.value = 0; safeStop(this.bypass, t + 0.05); }
    this.pushSchedule();
  }

  setPitch(hz: number, glideSec: number, t: number): void {
    this.semitones = 12 * Math.log2(effectiveHz(hz, this.cfg.cents) / this.sample.rootHz);
    glideTo(this.bypass.detune, this.semitones * 100, t, glideSec);   // harmless once the bypass has faded
    if (this.failed || !this.ready) return;
    audioLog.throttled('pitch', `ls${this.cfg.seed}`, 4, () => `live-stretch → ${fmtHz(hz)} (${this.semitones.toFixed(2)} st)`);
    this.pushSchedule();
  }

  attachDetuneModulator(node: AudioNode): void {
    // vibrato/drift ride on the sources' detune (a few cents): the shifter passes them through
    this.modulators.push(node);
    node.connect(this.src.detune);
    node.connect(this.bypass.detune);
  }

  stop(t: number): void { safeStop(this.src, t); safeStop(this.bypass, t); }

  dispose(): void {
    this.disposed = true;
    if (this.scheduleTimer) clearTimeout(this.scheduleTimer);
    const now = this.cfg.ctx.currentTime;
    safeStop(this.src, now); safeStop(this.bypass, now);
    for (const n of [this.src, this.bypass, this.bypassGain, this.nodeGain]) { try { n.disconnect(); } catch { /* gone */ } }
    if (this.node) { void this.node.stop().catch(() => undefined); try { this.node.disconnect(); } catch { /* gone */ } }
    try { this.output.disconnect(); } catch { /* gone */ }
  }
}
